import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { FormType } from '../types';
import { SummaryBlock } from '../comps';

const Div = styled.div`
  .summary {
    display: grid;
    grid-gap: 8px;
  }

  .value-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 30px;

    &__item {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 60px;
    }

    h6 {
      margin-top: 10px;
    }
  }
`;

interface Step6Props {
  onSubmit: () => void;
  onStep: (step: number) => void;
  onPrev: () => void;
  isSingleAccount: boolean;
}
const employmentStatusLabels = {
  '0': 'Employed',
  '1': 'Unemployed',
  '2': 'SelfEmployed',
  '3': 'Student',
  '4': 'Homemaker',
  '5': 'Retired',
  '6': 'Other',
};
const preferredCorrespondenceLabels = {
  '0': 'Email',
  '1': 'Post',
  '2': 'Phone',
};
const preferredContactTimeLabels = {
  '0': ' AM',
  '1': 'PM',
};
interface MarketingPreferenceLabels {
  [key: string]: string;
}
const marketingPreferenceLabels: MarketingPreferenceLabels = {
  '0': 'Telephone',
  '1': 'Email',
  '2': 'None',
};
interface AccountOpeningDetails {
  [key: string]: string;
}
const withdrawalSignaturesLabels: AccountOpeningDetails = {
  '0': 'One Signature',
  '1': 'Both/all signature',
};

const annualIncomeLabels = {
  '0': '£0 - £24,999',
  '1': '£25,000 - £49,999',
  '2': '£50,000 - £74,999',
  '3': '£150,000 - £299,999',
  '4': '£100,000 - £124,999',
  '5': '£125,000 - £149,999',
  '6': '£150,000 - £299,999',
  '7': '£300,000 +',
};

export const Step6: React.FC<Step6Props> = ({ onStep, onPrev, onSubmit, isSingleAccount }) => {
  const { values, errors } = useFormikContext<FormType>();

  return (
    <Div>
      <h4>Summary</h4>
      <div className="spc30" />
      <div className="summary">
        <SummaryBlock onEditClick={() => onStep(1)} title="Account Opening Details">
          <div className="value-grid">
            <div className="value-grid__item">
              <p>I/We would like to invest (amount)</p>
              <p>{values.accountOpeningDetails.investmentAmount}</p>
            </div>
            <div className="value-grid__item">
              <p>How much do you intend to deposit into this account in the future?</p>
              <p>{values.accountOpeningDetails.intendedDepositAmount}</p>
            </div>
            <div className="value-grid__item">
              <p>Source of initial deposit</p>
              <p>{values.accountOpeningDetails.initialDepositSource}</p>
            </div>
            <div className="value-grid__item">
              <p>Intended purpose of account</p>
              <p>{values.accountOpeningDetails.intendedAccountPurpose}</p>
            </div>
            <div className="value-grid__item">
              <p>Would you like a joint account?</p>
              <p>{values.accountOpeningDetails.jointAccountDetails ? 'Yes' : 'No'}</p>
            </div>
            {values.accountOpeningDetails.jointAccountDetails && (
              <>
                <div className="value-grid__item">
                  <p>What is your relationship between the account holders?</p>
                  <p>{values.accountOpeningDetails.jointAccountDetails?.accountHoldersRelationship}</p>
                </div>

                <h6>Withdrawal Instructions</h6>
                <div className="value-grid__item">
                  <p>Withdrawals allowed on signatures of:</p>
                  <p>
                    {withdrawalSignaturesLabels[values.accountOpeningDetails.jointAccountDetails.withdrawalSignatures]}
                  </p>
                </div>
                {/* <div className="value-grid__item">
                  <p>Correspondence Address</p>
                  <p>???</p>
                </div> */}
                <div className="value-grid__item">
                  <p>I/We apply to open a Account</p>
                  <p>Yes</p>
                </div>
              </>
            )}
          </div>
        </SummaryBlock>
        <SummaryBlock onEditClick={() => onStep(2)} title="Personal Details - Account Holder">
          <div className="value-grid">
            <div className="value-grid__item">
              <p>Are you an existing customer of Chorley Building Society?</p>
              <p>{values.accountHolderPersonalDetails.existingCustomer ? 'Yes' : 'No'}</p>
            </div>
            <div className="value-grid__item">
              <p>Title</p>

              <p>{values.accountHolderPersonalDetails.title.value}</p>
            </div>
            <div className="value-grid__item">
              <p>Forename(s)</p>
              <p>{values.accountHolderPersonalDetails.firstName}</p>
            </div>
            <div className="value-grid__item">
              <p>Surname</p>
              <p>{values.accountHolderPersonalDetails.lastName}</p>
            </div>
            <div className="value-grid__item">
              <p>Telephone Number</p>
              <p>{values.accountHolderPersonalDetails.contactNumber}</p>
            </div>
            <div className="value-grid__item">
              <p>Alternative Number</p>
              <p>{values.accountHolderPersonalDetails.alternativeContactNumber || '-'}</p>
            </div>
            <div className="value-grid__item">
              <p>Email Address</p>
              <p>{values.accountHolderPersonalDetails.emailAddress}</p>
            </div>
            <div className="value-grid__item">
              <p>Current Address</p>
              <p>
                {values.accountHolderPersonalDetails.currentAddress.line1}
                <br />
                {values.accountHolderPersonalDetails.currentAddress.line2}
                <br />
                {values.accountHolderPersonalDetails.currentAddress.city}
                <br />
                {values.accountHolderPersonalDetails.currentAddress.county}
                <br />
                {values.accountHolderPersonalDetails.currentAddress.postcode}
                <br />
                {values.accountHolderPersonalDetails.currentAddress.country}
              </p>
            </div>
            <div className="value-grid__item">
              <p>Nationality</p>
              <p>{values.accountHolderPersonalDetails.nationality}</p>
            </div>
            <div className="value-grid__item">
              <p>Country of Birth</p>
              <p>{values.accountHolderPersonalDetails.countryOfBirth}</p>
            </div>
            <div className="value-grid__item">
              <p>Date of Birth</p>
              <p>{new Date(values.accountHolderPersonalDetails.dateOfBirth).toDateString()}</p>
            </div>
            <div className="value-grid__item">
              <p>Preferred Correspondence</p>
              <p>{preferredCorrespondenceLabels[values.accountHolderPersonalDetails.preferredCorrespondence]}</p>
            </div>
            <div className="value-grid__item">
              <p>Preferred Contact Time</p>
              <p>{preferredContactTimeLabels[values.accountHolderPersonalDetails.preferredContactTime]}</p>
            </div>
            <h6>Tax Liabilities</h6>
            <div className="value-grid__item">
              <p>Are you a US citizen?</p>
              <p>{values.accountHolderPersonalDetails.usCitizen.value ? 'Yes' : 'No'}</p>
            </div>
            {values.accountHolderPersonalDetails.usCitizen.value && (
              <div className="value-grid__item">
                <p>Please provide your US Tax Identification No. (TIN)</p>
                <p>{values.accountHolderPersonalDetails.usCitizen.usTaxIdentificationNumber}</p>
              </div>
            )}
            <h6>Employment Status</h6>
            <div className="value-grid__item">
              <p>Employment Status</p>
              <p>{employmentStatusLabels[values.accountHolderPersonalDetails.employmentStatus.employmentStatus]}</p>
            </div>
            <div className="value-grid__item">
              <p>Occupation</p>
              <p>{values.accountHolderPersonalDetails.employmentStatus.occupation}</p>
            </div>
            <div className="value-grid__item">
              <p>Annual Income</p>
              <p>{annualIncomeLabels[values.accountHolderPersonalDetails.employmentStatus.annualIncome]}</p>
            </div>
            <div className="value-grid__item">
              <p>
                Do you currently, or have you ever, held a senior position of public trust in the UK or elsewhere (i.e.
                as a politician, councillor or judge whether full or part time, paid or unpaid) or are/were you closely
                connected to anyone that does or did hold one of these positions?
              </p>
              <p>{values.accountHolderPersonalDetails.employmentStatus.seniorPositionDetails.value ? 'Yes' : 'No'}</p>
            </div>
            {values.accountHolderPersonalDetails.employmentStatus.seniorPositionDetails.value && (
              <div className="value-grid__item">
                <p>Please provide further details</p>
                <p>{values.accountHolderPersonalDetails.employmentStatus.seniorPositionDetails.details}</p>
              </div>
            )}
          </div>
        </SummaryBlock>
        {!isSingleAccount && (
          <SummaryBlock onEditClick={() => onStep(3)} title="Personal Details - 2nd account Holder">
            <div className="value-grid">
              <div className="value-grid__item">
                <p>Are you an existing customer of Chorley Building Society?</p>
                <p>{values.secondAccountHolderPersonalDetails.existingCustomer ? 'Yes' : 'No'}</p>
              </div>
              <div className="value-grid__item">
                <p>Title</p>

                <p>{values.secondAccountHolderPersonalDetails.title.value}</p>
              </div>
              <div className="value-grid__item">
                <p>Forename(s)</p>
                <p>{values.secondAccountHolderPersonalDetails.firstName}</p>
              </div>
              <div className="value-grid__item">
                <p>Surname</p>
                <p>{values.secondAccountHolderPersonalDetails.lastName}</p>
              </div>
              <div className="value-grid__item">
                <p>Telephone Number</p>
                <p>{values.secondAccountHolderPersonalDetails.contactNumber}</p>
              </div>
              <div className="value-grid__item">
                <p>Alternative Number</p>
                <p>{values.secondAccountHolderPersonalDetails.alternativeContactNumber || '-'}</p>
              </div>
              <div className="value-grid__item">
                <p>Email Address</p>
                <p>{values.secondAccountHolderPersonalDetails.emailAddress}</p>
              </div>
              <div className="value-grid__item">
                <p>Current Address</p>
                <p>
                  {values.secondAccountHolderPersonalDetails.currentAddress.line1}
                  <br />
                  {values.secondAccountHolderPersonalDetails.currentAddress.line2}
                  <br />
                  {values.secondAccountHolderPersonalDetails.currentAddress.city}
                  <br />
                  {values.secondAccountHolderPersonalDetails.currentAddress.county}
                  <br />
                  {values.secondAccountHolderPersonalDetails.currentAddress.postcode}
                  <br />
                  {values.secondAccountHolderPersonalDetails.currentAddress.country}
                </p>
              </div>
              <div className="value-grid__item">
                <p>Nationality</p>
                <p>{values.secondAccountHolderPersonalDetails.nationality}</p>
              </div>
              <div className="value-grid__item">
                <p>Country of Birth</p>
                <p>{values.secondAccountHolderPersonalDetails.countryOfBirth}</p>
              </div>
              <div className="value-grid__item">
                <p>Date of Birth</p>
                <p>{new Date(values.secondAccountHolderPersonalDetails.dateOfBirth).toDateString()}</p>
              </div>
              <div className="value-grid__item">
                <p>Preferred Correspondence</p>
                <p>
                  {preferredCorrespondenceLabels[values.secondAccountHolderPersonalDetails.preferredCorrespondence]}
                </p>
              </div>
              <div className="value-grid__item">
                <p>Preferred Contact Time</p>
                <p>{preferredContactTimeLabels[values.secondAccountHolderPersonalDetails.preferredContactTime]}</p>
              </div>
              <h6>Tax Liabilities</h6>
              <div className="value-grid__item">
                <p>Are you a US citizen?</p>
                <p>{values.secondAccountHolderPersonalDetails.usCitizen.value ? 'Yes' : 'No'}</p>
              </div>
              {values.secondAccountHolderPersonalDetails.usCitizen.value && (
                <div className="value-grid__item">
                  <p>Please provide your US Tax Identification No. (TIN)</p>
                  <p>{values.secondAccountHolderPersonalDetails.usCitizen.usTaxIdentificationNumber}</p>
                </div>
              )}
              <h6>Employment Status</h6>
              <div className="value-grid__item">
                <p>Employment Status</p>
                <p>
                  {employmentStatusLabels[values.secondAccountHolderPersonalDetails.employmentStatus.employmentStatus]}
                </p>
              </div>
              <div className="value-grid__item">
                <p>Occupation</p>
                <p>{values.secondAccountHolderPersonalDetails.employmentStatus.occupation}</p>
              </div>
              <div className="value-grid__item">
                <p>Annual Income</p>
                <p>{annualIncomeLabels[values.secondAccountHolderPersonalDetails.employmentStatus.annualIncome]}</p>
              </div>
              <div className="value-grid__item">
                <p>
                  Do you currently, or have you ever, held a senior position of public trust in the UK or elsewhere
                  (i.e. as a politician, councillor or judge whether full or part time, paid or unpaid) or are/were you
                  closely connected to anyone that does or did hold one of these positions?
                </p>
                <p>
                  {values.secondAccountHolderPersonalDetails.employmentStatus.seniorPositionDetails.value
                    ? 'Yes'
                    : 'No'}
                </p>
              </div>
              {values.secondAccountHolderPersonalDetails.employmentStatus.seniorPositionDetails.value && (
                <div className="value-grid__item">
                  <p>Please provide further details</p>
                  <p>{values.secondAccountHolderPersonalDetails.employmentStatus.seniorPositionDetails.details}</p>
                </div>
              )}
            </div>
          </SummaryBlock>
        )}
        <SummaryBlock onEditClick={() => onStep(isSingleAccount ? 3 : 4)} title="Declaration">
          <div className="value-grid">
            <h6>Interest Instructions</h6>
            <div className="value-grid__item">
              <p>I/We require interest to be</p>
              <p>{values.interestRequirement.interestInstructions.value}</p>
            </div>
            <h6>Declaration</h6>
            <p>
              I/we acknowledge that transfers of fund into this account must originate from my personal sole and or
              joint current account or savings account.
            </p>
            <p>
              Any share(s) acquired by me/us under this account will not be held by me/us as a bare trustee for a body
              corporate, or for persons who include a body corporate.
            </p>
            <p>
              I/We have read a copy of the last published Summary Financial Statement. Copies are available on request
              or can be downloaded from our website www.chorleybs.co.uk.
            </p>
            <p>
              I/We have read a copy of and agree to be bound by the Society’s Memorandum of Rules. Copies are available
              on request or from our website www.chorleybs.co.uk.
            </p>
            <p>
              I/We agree to be bound by the conditions relating to the Agreement to Assign Windfalls to Charity. Copies
              are available on request or from our website www.chorleybs.co.uk.
            </p>
            <p>
              I/We confirm this information is correct and I/we understand that I/we may request in writing , a copy of
              the details, which are held about me/us on the Society’s records.
            </p>
            <p>
              I/We have read the Savings Account General Terms and Conditions and the marketing literature for the
              account I/we are applying for.
            </p>
            <p>
              I/We have read the Society’s Privacy Notice which describes the way my personal information will be
              processed.
            </p>
            <p>
              I/We acknowledge that the information contained in this form and information regarding reportable
              account(s) may be provided to the tax authorities of the country in which this account(s) is/are
              maintained and exchanged with tax authorities of another country or countries in which I/we may be tax
              resident pursuant to intergovernmental agreements to exchange financial account information.
            </p>
            <p>I/We acknowledge receipt of the FSCS information sheet.</p>
            <p>
              I/We undertake to advise Chorley Building Society within 30 days of any change in circumstances which
              affects my/our tax residency employmentStatus or causes information contained herein to become incorrect,
              and to provide Chorley Building Society with a suitably updated self-certification and interestRequirement
              within 30 days of such change in circumstances.
            </p>
          </div>
        </SummaryBlock>
        <SummaryBlock onEditClick={() => onStep(isSingleAccount ? 4 : 5)} title="Use of your Personal Information">
          <div className="value-grid">
            <div className="value-grid__item">
              <p>Applicant 1 Marketing Preference</p>
              <p>{marketingPreferenceLabels[values.personalInformation.applicant1MarketingPreference]}</p>
            </div>
            {values.personalInformation.applicant2MarketingPreference && (
              <div className="value-grid__item">
                <p>Applicant 2 Marketing Preference</p>
                <p>{marketingPreferenceLabels[values.personalInformation.applicant2MarketingPreference]}</p>
              </div>
            )}
          </div>
        </SummaryBlock>
      </div>
      <div className="spc30" />
      <p>
        Please note that on submitting this form we will send an email address to the primary applicant containing your
        submission ID which can be used if you have any queries regarding your application.
      </p>
      <div className="spc30" />
      <p>
        Following submission you will receive your account number and details of how to make your initial deposit
        through the post within the next 3 working days. Please be aware at this time your account cannot be operated
        via an online service. However, you can view your account by registering for our Chorley online service once the
        initial deposited has been received.
      </p>

      {/* END */}
      <div className="spc30" />
      <div className="spc10" />
      <div className="buttons">
        <button className="btn btn--secondary" onClick={onPrev}>
          Previous
        </button>
        <button className="btn" onClick={onSubmit} disabled={!!errors.personalInformation}>
          Submit
        </button>
      </div>
    </Div>
  );
};
